// react
import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';

// @material-ui/icons
import Announcement from "@material-ui/icons/Announcement";
import AttachFile from "@material-ui/icons/AttachFile";
import Check from "@material-ui/icons/Check";

// @material-ui components
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Chip from "@material-ui/core/Chip";

// core components
import GridContainer from "beaktShared/components/Grid/GridContainer.js";
import GridItem from "beaktShared/components/Grid/GridItem.js";
import Button from "beaktShared/components/CustomButtons/Button.js";
import Card from "beaktShared/components/Card/Card.js";
import CardBody from "beaktShared/components/Card/CardBody.js";
import CardIcon from "beaktShared/components/Card/CardIcon.js";
import CardHeader from "beaktShared/components/Card/CardHeader.js";
import Snackbar from "beaktShared/components/Snackbar/Snackbar.js";
import SnackbarContent from "beaktShared/components/Snackbar/SnackbarContent.js";
import ComunicadoConteudo from "beaktShared/components/TipTap/ComunicadoConteudo";
import TipoComunicadoBadge from "beaktShared/components/Comunicado/TipoComunicadoBadge";

// services
import * as MuralService from "./MuralService";
import { formateDate, formateDateandTime } from "beaktShared/helpers/masks";
import { SessionContext } from "beaktShared/services/session";

// styles
import { makeStyles } from "@material-ui/core/styles";
import styles from "assets/jss/material-dashboard-pro-react/views/regularFormsStyle";

const useStyles = makeStyles({
    ...styles,
    info: {
        color: "#777",
        fontSize: "13px",
        marginBottom: "12px",
        "& > span": {
            marginRight: "16px"
        }
    },
    conteudo: {
        border: "1px solid #e0e0e0",
        borderRadius: "5px",
        padding: "16px",
        marginBottom: "16px",
        maxHeight: "55vh",
        overflowY: "auto"
    },
    anexos: {
        marginBottom: "16px",
        "& > *": {
            margin: "0 8px 8px 0"
        }
    }
});

// Props: comunicado (item do mural), onAceito(aceite) apos registrar o aceite, handleView() para voltar
export default function MuralDetalhe({ comunicado, onAceito, handleView }) {
    const { t } = useTranslation();
    const classes = useStyles();
    const session = useContext(SessionContext);

    const [concordo, setConcordo] = useState(false);
    const [isConfirmando, setIsConfirmando] = useState(false);
    const [notification, setNotification] = useState({ show: false, color: 'info', message: '' });

    const showNotification = (message, color) => {
        setNotification({ show: true, color: color, message: message });
        setTimeout(() => setNotification({ show: false }), 6000);
    };

    const handleConfirmarLeitura = () => {
        if (!concordo || isConfirmando)
            return;

        setIsConfirmando(true);
        MuralService.aceitar(comunicado.comunicadoId, session.estabelecimentoId).then(result => {
            setIsConfirmando(false);
            if (!result.ok || !result.data) {
                if (result.message)
                    showNotification(result.message, 'danger');
                return;
            }
            onAceito(result.data);
        });
    };

    const renderRegistroAceite = () => (
        <SnackbarContent
            color="success"
            message={
                <span>
                    <b>{t('notice_board.acceptance_registered')}</b>
                    <br />
                    {t('notice_board.acceptance_record', {
                        user: session.nome,
                        date: formateDateandTime(comunicado.dataHoraAceite),
                        record: comunicado.comunicadoAceiteId
                    })}
                </span>
            }
        />
    );

    const renderAceite = () => (
        <div>
            <FormControlLabel
                control={
                    <Checkbox
                        tabIndex={-1}
                        checked={concordo}
                        disabled={isConfirmando}
                        onChange={e => setConcordo(e.target.checked)}
                        checkedIcon={<Check className={classes.checkedIcon} />}
                        icon={<Check className={classes.uncheckedIcon} />}
                        classes={{ checked: classes.checked, root: classes.checkRoot }}
                    />
                }
                classes={{ label: classes.label, root: classes.labelRoot }}
                label={t('notice_board.agree_checkbox')}
            />
            <div className={classes.textCenter}>
                <Button color="primary" disabled={!concordo || isConfirmando} onClick={handleConfirmarLeitura}>
                    {isConfirmando ? t('label.loading') + "..." : t('notice_board.confirm_reading')}
                </Button>
            </div>
        </div>
    );

    return (
        <div>
            <GridContainer>
                <GridItem xs={12}>
                    <Card>
                        <CardHeader color="primary" icon>
                            <CardIcon color="primary">
                                <Announcement />
                            </CardIcon>
                            <h4 className={classes.cardIconTitle}>{comunicado.titulo}</h4>
                        </CardHeader>
                        <CardBody>
                            <div className={classes.info}>
                                <span><TipoComunicadoBadge tipo={comunicado.tipo} /></span>
                                <span>{t('notice_board.published')}: {formateDate(comunicado.dataCriacao)}</span>
                                {comunicado.dataLimiteAceite &&
                                    <span>{t('notice_board.deadline')}: {formateDate(comunicado.dataLimiteAceite)}</span>}
                            </div>

                            <div className={classes.conteudo}>
                                <ComunicadoConteudo html={comunicado.descricao} />
                            </div>

                            {comunicado.anexos && comunicado.anexos.length > 0 &&
                                <div className={classes.anexos}>
                                    {comunicado.anexos.map(anexo => (
                                        <Chip
                                            key={anexo.comunicadoAnexoId}
                                            variant="outlined"
                                            color="primary"
                                            icon={<AttachFile />}
                                            label={anexo.nomeArquivo}
                                            clickable
                                            onClick={() => window.open(anexo.url, "_blank", "noopener,noreferrer")}
                                        />
                                    ))}
                                </div>}

                            {comunicado.aceito ? renderRegistroAceite() : renderAceite()}
                        </CardBody>
                    </Card>
                </GridItem>
            </GridContainer>

            <GridContainer justify="center">
                <GridItem xs={12} className={classes.textCenter}>
                    <Button color="transparent" disabled={isConfirmando} onClick={() => handleView()}>
                        {t('notice_board.back')}
                    </Button>
                </GridItem>
            </GridContainer>

            {notification.show &&
                <Snackbar
                    place="tr"
                    color={notification.color}
                    message={notification.message}
                    open={notification.show}
                    closeNotification={() => setNotification({ show: false })}
                    close
                />}
        </div>
    );
}
